import { ExternalLink } from "@/components/ExternalLink";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { CheckIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";

export type G4DynamicDatum = {
  id: number;
  strategy_id: number;
  strategy_name?: string | null;
  portfolio_id: number;
  portfolio_name?: string | null;
  date?: string | null;
  instrument_type?: string | null;
  unit_price?: number | null;
  cost_price?: number | null;
  accrued_interest?: number | null;
  percentage_price?: boolean | null;
  premium?: number | null;
  discount?: number | null;
  duration?: number | null;
  sensitivity?: number | null;
  credit_quality_step?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

const tableName = "g4_dynamic_data";
const t = (key: string, defaultValue: string) =>
  i18n.t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });

const boolCell =
  () =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ({ getValue }: any) =>
    Boolean(getValue()) && (
      <div className="flex justify-center">
        <CheckIcon />
      </div>
    );

export const g4_dynamic_data_columns: ColumnDef<G4DynamicDatum>[] = [
  {
    accessorKey: "id",
    header: t("id", "ID"),
  },
  {
    accessorKey: "portfolio_name",
    header: t("portfolio_name", "Portfolio"),
    cell: ({ row }) => {
      const id = row.original.portfolio_id;
      const name = row.original.portfolio_name;
      if (!id) return "";
      return (
        <ExternalLink
          href={toWorkspacePath(`/admin/qtl - referential/strategies/${id}`)}
          className="text-xs"
          underline="hover"
        >
          {name || id}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: "strategy_name",
    header: t("strategy_name", "Strategy"),
    cell: ({ row }) => {
      const id = row.original.strategy_id;
      const name = row.original.strategy_name;
      if (!id) return "";
      return (
        <ExternalLink
          href={toWorkspacePath(`/admin/qtl - referential/strategies/${id}`)}
          className="text-xs"
          underline="hover"
        >
          {name || id}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: "date",
    header: t("date", "Date"),
    cell: ({ row }) => {
      const value = row.getValue("date");
      return value ? parseDate(String(value)) : "";
    },
  },
  {
    accessorKey: "instrument_type",
    header: t("instrument_type", "Instrument type"),
  },
  {
    accessorKey: "unit_price",
    header: () => (
      <div className="text-right">{t("unit_price", "Unit price")}</div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue("unit_price"))}
      </div>
    ),
  },
  {
    accessorKey: "cost_price",
    header: () => (
      <div className="text-right">{t("cost_price", "Cost price")}</div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue("cost_price"))}
      </div>
    ),
  },
  {
    accessorKey: "accrued_interest",
    header: () => (
      <div className="text-right">
        {t("accrued_interest", "Accrued interest")}
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue("accrued_interest"))}
      </div>
    ),
  },
  {
    accessorKey: "percentage_price",
    header: () => (
      <div className="text-center">
        {t("percentage_price", "Percentage price")}
      </div>
    ),
    cell: boolCell(),
  },
  {
    accessorKey: "premium",
    header: () => <div className="text-right">{t("premium", "Premium")}</div>,
    cell: ({ row }) => (
      <div className="text-right">{formatNumber(row.getValue("premium"))}</div>
    ),
  },
  {
    accessorKey: "discount",
    header: () => <div className="text-right">{t("discount", "Discount")}</div>,
    cell: ({ row }) => (
      <div className="text-right">{formatNumber(row.getValue("discount"))}</div>
    ),
  },
  {
    accessorKey: "duration",
    header: () => <div className="text-right">{t("duration", "Duration")}</div>,
    cell: ({ row }) => (
      <div className="text-right">{formatNumber(row.getValue("duration"))}</div>
    ),
  },
  {
    accessorKey: "sensitivity",
    header: () => (
      <div className="text-right">{t("sensitivity", "Sensitivity")}</div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue("sensitivity"))}
      </div>
    ),
  },
  {
    accessorKey: "credit_quality_step",
    header: t("credit_quality_step", "Cqs"),
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
